import { Fragment, useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { formatISO, endOfDay } from 'date-fns';
import { DialogHeader, DialogTitle } from '@ui/dialog.tsx';
import { Button } from '@ui/button.tsx';
import { Form } from '@ui/form.tsx';
import { ChevronLeft, ChevronRight, CheckCircle2 } from 'lucide-react';
import { defineStepper } from '@ui/stepper.tsx';
import { useTranslation } from 'react-i18next';
import { useUser } from '@r/api/auth.tsx';
import { useQueryClient } from '@tanstack/react-query';
import { UseWorkspace } from '@r/hooks/use-workspace.ts';
import {
  useApiAdminlogicsworkspacesPost,
  getApiAdminlogicsworkspacesGetCollectionQueryKey
} from '@r/api/generated/client.ts';
import { useOrvalErrorToast } from '@r/hooks/use-orval-error-toast.ts';
import { LOGIC_TYPE_TO_IRI } from '@admin/types/workspace/logic.ts';
import {
  LogicLogicReadModelType,
  type LogicWorkspaceWorkspaceReadModel
} from '@r/api/generated/client.schemas.ts';
import {
  buildRenewalFormSchema,
  getRenewalFormDefaultValues,
  type RenewalProgramFormSchema
} from '@admin/components/renew-program/renewal-program-form-schema.ts';
import { RenewalProgramLogicStepContent } from './renewal-program-logic-step-content.tsx';
import { RenewalProgramStepsStepContent } from './renewal-program-steps-step-content.tsx';
import { RenewalProgramConfirmationStepContent } from './renewal-program-confirmation-step-content.tsx';

const standardRenewalStepper = defineStepper(
  { id: 'step-logic', title: 'form.workspace.renewal.step.logic' },
  { id: 'step-confirmation', title: 'form.workspace.renewal.step.confirmation' }
);

const stepLogicRenewalStepper = defineStepper(
  { id: 'step-logic', title: 'form.workspace.renewal.step.logic' },
  { id: 'step-paliers', title: 'form.workspace.renewal.step.paliers' },
  { id: 'step-confirmation', title: 'form.workspace.renewal.step.confirmation' }
);

interface RenewalProgramFormStepProps {
  onFinish: () => void;
  onBack: () => void;
}

/** Dialog de renouvellement : stepper 2 ou 3 étapes selon logique STEP, puis soumission API. */
export const RenewalProgramFormStep = ({ onFinish, onBack }: RenewalProgramFormStepProps) => {
  const { t } = useTranslation();
  const showOrvalErrorToast = useOrvalErrorToast();
  const user = useUser();
  const queryClient = useQueryClient();
  const { workspace, isWorkspacePending } = UseWorkspace(user.data ?? null);
  const currentLogic = workspace?.currentLogicWorkspace ?? null;
  const isStepProgram = currentLogic?.logic?.type === LogicLogicReadModelType.STEP;

  const submitMutation = useApiAdminlogicsworkspacesPost();

  const formSchema = useMemo(
    () => buildRenewalFormSchema(t, currentLogic?.endAt, isStepProgram),
    [t, currentLogic?.endAt, isStepProgram]
  );
  const defaultValues = useMemo(() => getRenewalFormDefaultValues(currentLogic), [currentLogic]);

  const form = useForm<RenewalProgramFormSchema>({
    resolver: zodResolver(formSchema),
    defaultValues,
    mode: 'onChange'
  });

  /** Corps POST logicsWorkspaces : dates ISO, expiration, paliers STEP ou targets inchangés. */
  const buildPayload = useCallback(() => {
    const values = form.getValues();
    const logicType = currentLogic?.logic?.type;
    const yearOffset = values.pointExpirationYearOffset ?? 0;
    const yearUnit = yearOffset === 1 ? 'year' : 'years';
    const pointExpirationDate =
      values.pointExpirationType === 'yearEnd'
        ? yearOffset > 0
          ? `last day of December this year + ${yearOffset} ${yearUnit}, 23:59:59`
          : 'last day of December this year, 23:59:59'
        : values.pointExpirationDate
          ? formatISO(endOfDay(values.pointExpirationDate))
          : 'last day of December this year, 23:59:59';

    const logicIri = logicType
      ? LOGIC_TYPE_TO_IRI[logicType as LogicLogicReadModelType]
      : undefined;
    if (!logicIri || !currentLogic) {
      throw new Error('Logique ou workspace actuel manquant');
    }

    const steps =
      logicType === LogicLogicReadModelType.STEP && values.steps?.length
        ? values.steps.map((step) => ({
            name: step.name,
            description: step.description ?? null,
            stepPoint: step.stepPoint
          }))
        : undefined;
    const targets =
      (logicType === LogicLogicReadModelType.TARGET_PERF ||
        logicType === LogicLogicReadModelType.TARGET_POINT) &&
      currentLogic.targets?.length
        ? currentLogic.targets.map((targetItem) => ({
            reference: targetItem.reference,
            description: targetItem.description ?? undefined
          }))
        : undefined;

    return {
      logic: logicIri,
      startAt: formatISO(values.startAt),
      endAt: formatISO(values.endAt),
      pointExpirationDate,
      ...(steps?.length ? { steps } : {}),
      ...(targets?.length ? { targets } : {})
    };
  }, [form, currentLogic]);

  const handleConfirmSubmit = useCallback(async () => {
    const valid = await form.trigger();
    if (!valid) return;
    try {
      await submitMutation.mutateAsync({ data: buildPayload() });
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: getApiAdminlogicsworkspacesGetCollectionQueryKey()
        }),
        queryClient.invalidateQueries({
          queryKey: ['workspace', user.data?.id, workspace?.id?.toString() ?? '']
        })
      ]);
      onFinish();
    } catch (error) {
      showOrvalErrorToast(error, t('form.workspace.renewal.error.fallback'));
    }
  }, [
    form,
    buildPayload,
    submitMutation,
    queryClient,
    user.data?.id,
    workspace?.id,
    onFinish,
    showOrvalErrorToast,
    t
  ]);

  if (isWorkspacePending || !currentLogic) {
    return (
      <>
        <DialogHeader>
          <DialogTitle className="text-2xl">{t('form.workspace.renewal.dialog.title')}</DialogTitle>
        </DialogHeader>
        <div className="flex justify-center items-center py-16 text-muted-foreground">
          {t('common.loading')}
        </div>
      </>
    );
  }

  const sharedStepperProps = {
    form,
    currentLogic,
    onConfirmSubmit: handleConfirmSubmit,
    onBack,
    isSubmitting: submitMutation.isPending
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle className="text-2xl">{t('form.workspace.renewal.dialog.title')}</DialogTitle>
      </DialogHeader>

      <Form {...form}>
        {isStepProgram ? (
          <StepLogicRenewalStepper {...sharedStepperProps} />
        ) : (
          <StandardRenewalStepper {...sharedStepperProps} />
        )}
      </Form>
    </>
  );
};

type RenewalStepperSharedProps = {
  form: ReturnType<typeof useForm<RenewalProgramFormSchema>>;
  currentLogic: LogicWorkspaceWorkspaceReadModel;
  onConfirmSubmit: () => Promise<void>;
  onBack: () => void;
  isSubmitting: boolean;
};

const {
  StepperProvider: StandardStepperProvider,
  StepperNavigation: StandardStepperNavigation,
  StepperStep: StandardStepperStep,
  StepperTitle: StandardStepperTitle,
  StepperPanel: StandardStepperPanel,
  StepperControls: StandardStepperControls
} = standardRenewalStepper;

/** Renouvellement linear / objectifs : logique → confirmation. */
function StandardRenewalStepper({
  form,
  currentLogic,
  onConfirmSubmit,
  onBack,
  isSubmitting
}: RenewalStepperSharedProps) {
  const { t } = useTranslation();

  return (
    <StandardStepperProvider variant="horizontal" className="mt-6">
      {({ methods }) => (
        <Fragment>
          <StandardStepperNavigation className="mb-8">
            {methods.all.map((step) => (
              <StandardStepperStep key={step.id} of={step.id}>
                <StandardStepperTitle>{t(step.title)}</StandardStepperTitle>
              </StandardStepperStep>
            ))}
          </StandardStepperNavigation>

          <div className="min-h-[300px] py-4">
            {methods.switch({
              'step-logic': () => (
                <StandardStepperPanel>
                  <RenewalProgramLogicStepContent form={form} currentLogic={currentLogic!} />
                </StandardStepperPanel>
              ),
              'step-confirmation': () => (
                <StandardStepperPanel>
                  <RenewalProgramConfirmationStepContent form={form} currentLogic={currentLogic!} />
                </StandardStepperPanel>
              )
            })}
          </div>

          <StandardStepperControls className="pt-6">
            <NavigationButtons
              form={form}
              methods={methods}
              onConfirmSubmit={onConfirmSubmit}
              onBack={onBack}
              isSubmitting={isSubmitting}
              validatePaliersStep={false}
            />
          </StandardStepperControls>
        </Fragment>
      )}
    </StandardStepperProvider>
  );
}

const {
  StepperProvider: StepLogicStepperProvider,
  StepperNavigation: StepLogicStepperNavigation,
  StepperStep: StepLogicStepperStep,
  StepperTitle: StepLogicStepperTitle,
  StepperPanel: StepLogicStepperPanel,
  StepperControls: StepLogicStepperControls
} = stepLogicRenewalStepper;

/** Renouvellement STEP : logique → paliers → confirmation. */
function StepLogicRenewalStepper({
  form,
  currentLogic,
  onConfirmSubmit,
  onBack,
  isSubmitting
}: RenewalStepperSharedProps) {
  const { t } = useTranslation();

  return (
    <StepLogicStepperProvider variant="horizontal" className="mt-6">
      {({ methods }) => (
        <Fragment>
          <StepLogicStepperNavigation className="mb-8">
            {methods.all.map((step) => (
              <StepLogicStepperStep key={step.id} of={step.id}>
                <StepLogicStepperTitle>{t(step.title)}</StepLogicStepperTitle>
              </StepLogicStepperStep>
            ))}
          </StepLogicStepperNavigation>

          <div className="min-h-[300px] py-4">
            {methods.switch({
              'step-logic': () => (
                <StepLogicStepperPanel>
                  <RenewalProgramLogicStepContent form={form} currentLogic={currentLogic!} />
                </StepLogicStepperPanel>
              ),
              'step-paliers': () => (
                <StepLogicStepperPanel>
                  <RenewalProgramStepsStepContent form={form} />
                </StepLogicStepperPanel>
              ),
              'step-confirmation': () => (
                <StepLogicStepperPanel>
                  <RenewalProgramConfirmationStepContent form={form} currentLogic={currentLogic!} />
                </StepLogicStepperPanel>
              )
            })}
          </div>

          <StepLogicStepperControls className="pt-6">
            <NavigationButtons
              form={form}
              methods={methods}
              onConfirmSubmit={onConfirmSubmit}
              onBack={onBack}
              isSubmitting={isSubmitting}
              validatePaliersStep
            />
          </StepLogicStepperControls>
        </Fragment>
      )}
    </StepLogicStepperProvider>
  );
}

/** Valide l’étape courante (champs ou `steps`) avant d’avancer ou de confirmer. */
const NavigationButtons = ({
  form,
  methods,
  onConfirmSubmit,
  onBack,
  isSubmitting,
  validatePaliersStep
}: {
  form: ReturnType<typeof useForm<RenewalProgramFormSchema>>;
  methods: {
    current: { id: string };
    all: { id: string }[];
    next: () => void;
    prev: () => void;
  };
  onConfirmSubmit: () => Promise<void>;
  onBack: () => void;
  isSubmitting: boolean;
  validatePaliersStep: boolean;
}) => {
  const { t } = useTranslation();
  const currentIndex = methods.all.findIndex((step) => step.id === methods.current.id);
  const isFirstStep = currentIndex === 0;
  const isLastStep = currentIndex === methods.all.length - 1;

  const handleNext = async () => {
    if (isLastStep) {
      await onConfirmSubmit();
      return;
    }
    if (methods.current.id === 'step-logic') {
      const valid = await form.trigger([
        'startAt',
        'endAt',
        'pointExpirationType',
        'pointExpirationDate',
        'pointExpirationYearOffset'
      ]);
      if (!valid) return;
    }
    if (validatePaliersStep && methods.current.id === 'step-paliers') {
      const valid = await form.trigger('steps');
      if (!valid) return;
    }
    methods.next();
  };

  return (
    <>
      {isFirstStep ? (
        <Button
          type="button"
          variant="outline"
          rounded="full"
          onClick={onBack}
          className="flex gap-2 items-center"
          disabled={isSubmitting}
        >
          <ChevronLeft className="w-4 h-4" />
          {t('common.back')}
        </Button>
      ) : (
        <Button
          type="button"
          rounded="full"
          variant="outline"
          onClick={() => methods.prev()}
          className="flex gap-2 items-center"
          disabled={isSubmitting}
        >
          <ChevronLeft className="w-4 h-4" />
          {t('common.previous')}
        </Button>
      )}

      {isLastStep ? (
        <Button
          type="button"
          onClick={handleNext}
          rounded="full"
          className="flex gap-2 items-center"
          disabled={isSubmitting}
        >
          <CheckCircle2 />
          {isSubmitting ? t('common.sending') : t('form.workspace.renewal.confirm.cta')}
        </Button>
      ) : (
        <Button
          type="button"
          rounded="full"
          onClick={handleNext}
          className="flex gap-2 items-center"
        >
          {t('common.next')}
          <ChevronRight className="w-4 h-4" />
        </Button>
      )}
    </>
  );
};
